"use client";

import { useEffect } from "react";

const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 10;
const DEDUP_TTL_MS = 30_000;

const EMAIL_RE = /([A-Z0-9._%+-]+)@([A-Z0-9.-]+\.[A-Z]{2,})/gi;
const TOKENISH_RE = /\b(eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+)\b/g;

function scrubString(s: string) {
    return s.replace(EMAIL_RE, "***@***").replace(TOKENISH_RE, "***.***.***");
}

function safeStringify(x: any) {
    try {
        if (typeof x === "string") return scrubString(x);
        if (x instanceof Error) return scrubString(`${x.name}: ${x.message}\n${x.stack ?? ""}`);
        return scrubString(JSON.stringify(x));
    } catch {
        return scrubString(String(x));
    }
}

function hashKey(type: string, message: string, url: string) {
    // semplice chiave dedup (non crittografica)
    return `${type}::${message.slice(0, 200)}::${url.slice(0, 200)}`;
}

export default function ClientLogging() {
    useEffect(() => {
        let sent = 0;
        let resetAt = Date.now() + WINDOW_MS;

        const dedup = new Map<string, number>();

        function canSend(key: string) {
            const now = Date.now();
            if (now >= resetAt) {
                sent = 0;
                resetAt = now + WINDOW_MS;
            }
            // dedup
            const last = dedup.get(key);
            if (last && now - last < DEDUP_TTL_MS) return false;
            // rate limit
            if (sent >= MAX_PER_WINDOW) return false;

            dedup.set(key, now);
            sent += 1;
            return true;
        }

        function send(payload: any) {
            const body = JSON.stringify(payload);
            if (body.length > 20_000) return; // safety

            if (navigator.sendBeacon) {
                navigator.sendBeacon("/api/client-log", body);
                return;
            }
            fetch("/api/client-log", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body,
                keepalive: true,
            }).catch(() => { });
        }

        const origError = console.error;
        console.error = (...args: any[]) => {
            try {
                const message = args.map(safeStringify).join(" ");
                const key = hashKey("console.error", message, location.href);
                if (canSend(key)) {
                    send({
                        level: "error",
                        type: "console.error",
                        message,
                        url: location.href,
                        userAgent: navigator.userAgent,
                    });
                }
            } catch { }
            origError(...args);
        };

        const onError = (event: ErrorEvent) => {
            const message = scrubString(event.message || "window.error");
            const key = hashKey("window.error", message, location.href);
            if (!canSend(key)) return;

            send({
                level: "error",
                type: "window.error",
                message,
                url: location.href,
                userAgent: navigator.userAgent,
                error: {
                    filename: scrubString(event.filename || ""),
                    lineno: event.lineno,
                    colno: event.colno,
                    stack: scrubString(event.error?.stack || ""),
                },
            });
        };

        const onRejection = (event: PromiseRejectionEvent) => {
            const msg = "unhandledrejection";
            const detail = safeStringify(event.reason);
            const key = hashKey("unhandledrejection", detail, location.href);
            if (!canSend(key)) return;

            send({
                level: "error",
                type: "unhandledrejection",
                message: msg,
                detail,
                url: location.href,
                userAgent: navigator.userAgent,
            });
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);

        return () => {
            console.error = origError;
            window.removeEventListener("error", onError);
            window.removeEventListener("unhandledrejection", onRejection);
        };
    }, []);

    return null;
}